import { useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Container, IconButton, Stack, Typography } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { useAuth } from '../../shared/hooks/useAuth';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import { PostForm } from './components/PostForm';
import { useOpportunityMutations } from './useOpportunities';

/**
 * Standalone "new post" page. Authenticated route — unauthenticated visitors
 * are bounced to /login with a return path so they land back here after
 * sign-in. The form itself owns the field state; this page only orchestrates
 * the create mutation, navigation, and toast.
 */
export function CreateOpportunityPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const { create } = useOpportunityMutations();

  // Bounce anonymous visitors to login with a return path so the post-login
  // redirect drops them back on this page.
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login?next=' + encodeURIComponent('/opportunities/new'), {
        replace: true,
      });
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading || !isAuthenticated) return null;

  const errorMessage = create.error ? translate(create.error) : null;

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, md: 5 }, px: { xs: 0, sm: 2, md: 3 } }}>
      <Stack direction="row" alignItems="center" sx={{ px: { xs: 1, sm: 0 }, mb: 1 }}>
        <IconButton
          component={RouterLink}
          to="/opportunities"
          aria-label={t('common.back')}
          edge="start"
        >
          <ArrowBackRoundedIcon />
        </IconButton>
      </Stack>

      <Box
        sx={{
          bgcolor: 'background.paper',
          border: { xs: 'none', sm: '1px solid' },
          borderColor: 'divider',
          borderRadius: { xs: 0, sm: 3 },
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 800,
            letterSpacing: -0.3,
            fontSize: { xs: 22, md: 28 },
            mb: 2.5,
          }}
        >
          {t('opportunities.form.newTitle')}
        </Typography>

        <PostForm
          submitting={create.isPending}
          submitLabel={t('opportunities.form.publish')}
          submittingLabel={t('opportunities.form.publishing')}
          errorMessage={errorMessage}
          onCancel={() => navigate('/opportunities')}
          onSubmit={async (body) => {
            try {
              const post = await create.mutateAsync(body);
              navigate(`/opportunities/${post.id}`, { replace: true });
            } catch (err) {
              notify.error(translate(err));
            }
          }}
        />
      </Box>
    </Container>
  );
}
